import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, Save, Check, Clock, User, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DAY_TYPES = [
  { key: "normal_working_day", label: "Normal Working Day" },
  { key: "regular_holiday", label: "Regular Holiday" },
  { key: "special_holiday", label: "Special Holiday" },
];

const EMPLOYEE_DAY_TYPES = [
  { key: "work_day", label: "Work Day" },
  { key: "rest_day", label: "Rest Day" },
  { key: "leave_without_pay", label: "Leave Without Pay" },
  { key: "leave_with_pay", label: "Leave With Pay" },
  { key: "absent", label: "Absent" },
  { key: "training_day", label: "Training Day" },
];

// Convert "HH:MM" -> minutes-since-midnight
function timeToMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}
function minutesToTime(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Build slots from start to end at every `interval` minutes (inclusive of end)
function buildSlots(start, end, interval) {
  const s = timeToMinutes(start || "07:00");
  const e = timeToMinutes(end || "22:00");
  const step = Math.max(5, Number(interval) || 30);
  if (e <= s) return [];
  const out = [];
  for (let m = s; m <= e; m += step) out.push(minutesToTime(m));
  return out;
}

function formatLongDate(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function pad(n) { return String(n).padStart(2, "0"); }
function shiftYmd(ymd, delta) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

export default function EmployeeAttendanceDayPage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const { selectedBranch, selectedSchool, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const { user } = useAuth();

  const timeSlots = useMemo(
    () => buildSlots(
      user?.attendance_start || "07:00",
      user?.attendance_end || "22:00",
      user?.attendance_interval_minutes || 30,
    ),
    [user?.attendance_start, user?.attendance_end, user?.attendance_interval_minutes]
  );
  const intervalMinutes = user?.attendance_interval_minutes || 30;

  const [employees, setEmployees] = useState([]);
  const [dayType, setDayType] = useState("normal_working_day");
  // employeeMap: { [employee_id]: { employee_day_type, slots: {time: 'present'|'late'} } }
  const [employeeMap, setEmployeeMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!selectedBranch) return;
    setLoading(true);
    try {
      const [emp, day] = await Promise.all([
        api.get("/employees", { params: { branch_id: selectedBranch.id } }),
        api.get("/attendance/day", { params: { branch_id: selectedBranch.id, date } }),
      ]);
      setEmployees(emp.data || []);
      setDayType(day.data?.day_type || "normal_working_day");
      const m = {};
      (day.data?.employees || []).forEach((e) => {
        m[e.employee_id] = {
          employee_day_type: e.employee_day_type || "work_day",
          slots: e.slots || {},
        };
      });
      setEmployeeMap(m);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, date]);

  useEffect(() => { load(); }, [load]);

  const updateEmployee = (employeeId, patch) => {
    setEmployeeMap((prev) => ({
      ...prev,
      [employeeId]: {
        employee_day_type: prev[employeeId]?.employee_day_type || "work_day",
        slots: prev[employeeId]?.slots || {},
        ...patch,
      },
    }));
  };

  const updateSlot = (employeeId, time, mark) => {
    setEmployeeMap((prev) => {
      const current = prev[employeeId] || { employee_day_type: "work_day", slots: {} };
      const nextSlots = { ...(current.slots || {}) };
      if (mark === null || mark === undefined) {
        delete nextSlots[time];
      } else {
        nextSlots[time] = mark;
      }
      return { ...prev, [employeeId]: { ...current, slots: nextSlots } };
    });
  };

  const save = async () => {
    if (!selectedBranch) return;
    setSaving(true);
    try {
      const payload = {
        branch_id: selectedBranch.id,
        date,
        day_type: dayType,
        employees: employees.map((emp) => ({
          employee_id: emp.id,
          employee_day_type: employeeMap[emp.id]?.employee_day_type || "work_day",
          slots: employeeMap[emp.id]?.slots || {},
        })),
      };
      await api.put("/attendance/day", payload);
      toast.success("Attendance saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">
            Employee Attendance Module
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <button
              onClick={() => navigate(`/employees/attendance/${shiftYmd(date, -1)}`)}
              className="w-8 h-8 rounded-lg border border-brand-purple/20 flex items-center justify-center text-brand-inkMuted hover:bg-brand-purpleLight hover:text-foreground transition-colors"
              aria-label="Previous day"
              data-testid="prev-day-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <p className="text-brand-inkMuted font-heading text-lg" data-testid="attendance-date-label">
              {formatLongDate(date)}
            </p>
            <button
              onClick={() => navigate(`/employees/attendance/${shiftYmd(date, 1)}`)}
              className="w-8 h-8 rounded-lg border border-brand-purple/20 flex items-center justify-center text-brand-inkMuted hover:bg-brand-purpleLight hover:text-foreground transition-colors"
              aria-label="Next day"
              data-testid="next-day-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {selectedSchool && (
            <p className="text-sm text-brand-inkMuted mt-0.5">
              School: <span className="font-semibold text-foreground">{selectedSchool.name}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/employees/attendance")}
            className="rounded-2xl border-brand-purple/30"
            data-testid="back-to-calendar-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Calendar
          </Button>
          <Button
            onClick={save}
            disabled={saving || !selectedBranch}
            className="rounded-2xl bg-[#1976D2] hover:bg-[#1565C0] text-white"
            data-testid="save-attendance-btn"
          >
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Attendance"}
          </Button>
        </div>
      </div>

      {!selectedBranch ? (
        <NoBranchCard />
      ) : (
        <>
          <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-6 mb-6" data-testid="attendance-filters">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-heading text-sm text-brand-inkMuted">Branch</label>
                <select
                  value={selectedBranch.id}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  className="w-full mt-1.5 h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                  data-testid="branch-select"
                >
                  {branchesForSelectedSchool.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-heading text-sm text-brand-inkMuted">Day Type</label>
                <select
                  value={dayType}
                  onChange={(e) => setDayType(e.target.value)}
                  className="w-full mt-1.5 h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                  data-testid="day-type-select"
                >
                  {DAY_TYPES.map((d) => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {loading ? (
            <p className="text-brand-inkMuted">Loading...</p>
          ) : employees.length === 0 ? (
            <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center" data-testid="no-employees">
              <p className="font-heading font-semibold text-foreground">No employees yet</p>
              <p className="text-brand-inkMuted mt-1 text-sm">Add employees in Employee List to start tracking attendance.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {employees.map((emp) => (
                <EmployeeAttendanceCard
                  key={emp.id}
                  employee={emp}
                  entry={employeeMap[emp.id] || { employee_day_type: "work_day", slots: {} }}
                  timeSlots={timeSlots}
                  intervalMinutes={intervalMinutes}
                  onUpdate={(patch) => updateEmployee(emp.id, patch)}
                  onSlotChange={(time, mark) => updateSlot(emp.id, time, mark)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function EmployeeAttendanceCard({ employee, entry, onUpdate, onSlotChange, timeSlots, intervalMinutes }) {
  // Split slots into two rows of about half each
  const half = Math.ceil(timeSlots.length / 2);
  const row1 = timeSlots.slice(0, half);
  const row2 = timeSlots.slice(half);

  // ---- Drag-to-paint state ------------------------------------------------
  // Click-hold-drag across hour chips to bulk-mark or bulk-clear.
  //   - starts on empty chip -> paint "present"
  //   - starts on present chip -> clear
  //   - starts on late chip   -> clear
  // A single click (no drag) still opens the Popover for the full menu
  // (Present / Late / Clear), so nothing about the existing UX regresses.
  const dragRef = useRef({ active: false, paintMode: null, startTime: null, startMark: null, moved: false });

  useEffect(() => {
    const reset = () => {
      // We defer clearing `moved` by one tick so onClick handlers running
      // right after mouseup can still see it and suppress the Popover.
      const wasMoved = dragRef.current.moved;
      dragRef.current.active = false;
      dragRef.current.startTime = null;
      dragRef.current.paintMode = null;
      if (wasMoved) {
        setTimeout(() => { dragRef.current.moved = false; }, 0);
      } else {
        dragRef.current.moved = false;
      }
    };
    window.addEventListener("pointerup", reset);
    window.addEventListener("pointercancel", reset);
    return () => {
      window.removeEventListener("pointerup", reset);
      window.removeEventListener("pointercancel", reset);
    };
  }, []);

  const beginDrag = useCallback((time, mark) => {
    dragRef.current = {
      active: true,
      startTime: time,
      startMark: mark || null,
      // Empty or "late" -> paint present. Already present -> clear.
      paintMode: mark === "present" ? "clear" : "mark_present",
      moved: false,
    };
  }, []);

  const paintChip = useCallback((time, mark) => {
    if (!dragRef.current.active) return;
    // Never repaint the starting chip until we've moved off it and back
    // (avoids toggling twice for a still cursor).
    if (dragRef.current.startTime === time && !dragRef.current.moved) return;

    if (!dragRef.current.moved) {
      dragRef.current.moved = true;
      // First real drag hop: also apply paint to the START chip.
      if (dragRef.current.paintMode === "mark_present") {
        onSlotChange(dragRef.current.startTime, "present");
      } else {
        onSlotChange(dragRef.current.startTime, null);
      }
    }
    if (dragRef.current.paintMode === "mark_present") {
      if (mark !== "present") onSlotChange(time, "present");
    } else if (dragRef.current.paintMode === "clear") {
      if (mark !== null && mark !== undefined) onSlotChange(time, null);
    }
  }, [onSlotChange]);

  const wasDraggedRef = () => dragRef.current.moved;
  // -------------------------------------------------------------------------

  // Compute end-of-slot label given the next slot or the interval
  const endTimeFor = (t) => {
    const idx = timeSlots.indexOf(t);
    if (idx === -1) return null;
    const next = timeSlots[idx + 1];
    if (next) return next;
    return minutesToTime(timeToMinutes(t) + (intervalMinutes || 30));
  };

  return (
    <article className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-6" data-testid={`attendance-card-${employee.id}`}>
      <header className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          {employee.picture ? (
            <img src={employee.picture} alt={employee.name}
              className="w-14 h-14 rounded-2xl object-cover border border-brand-purple/10" />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-brand-purpleLight flex items-center justify-center">
              <User className="w-7 h-7 text-brand-purple" />
            </div>
          )}
          <div>
            <h3 className="font-heading text-lg sm:text-xl font-semibold tracking-wide text-foreground uppercase"
              data-testid={`attendance-employee-name-${employee.id}`}>
              {employee.name}
            </h3>
            <p className="text-xs sm:text-sm text-brand-inkMuted uppercase tracking-wider">
              {employee.position || ""}
            </p>
          </div>
        </div>
        <select
          value={entry.employee_day_type}
          onChange={(e) => onUpdate({ employee_day_type: e.target.value })}
          className="h-11 rounded-2xl border border-brand-purple/20 bg-white px-4 font-body text-foreground min-w-[180px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
          data-testid={`employee-day-type-${employee.id}`}
        >
          {EMPLOYEE_DAY_TYPES.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
      </header>

      <div className="border-t border-brand-purple/10 pt-4">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-1">
          <p className="font-heading text-xs font-semibold text-brand-inkMuted tracking-wider">HOURS</p>
          <p className="text-[10px] text-brand-inkMuted">
            <span className="hidden sm:inline">Tip: click-and-drag across hours to bulk-mark or bulk-clear.</span>
          </p>
        </div>
        <div className="space-y-2 select-none">
          <SlotRow times={row1} slots={entry.slots} endTimeFor={endTimeFor} onChange={onSlotChange} beginDrag={beginDrag} paintChip={paintChip} wasDraggedRef={wasDraggedRef} testIdPrefix={`row1-${employee.id}`} />
          <SlotRow times={row2} slots={entry.slots} endTimeFor={endTimeFor} onChange={onSlotChange} beginDrag={beginDrag} paintChip={paintChip} wasDraggedRef={wasDraggedRef} testIdPrefix={`row2-${employee.id}`} />
        </div>
      </div>
    </article>
  );
}

function SlotRow({ times, slots, onChange, endTimeFor, testIdPrefix, beginDrag, paintChip, wasDraggedRef }) {
  return (
    <div className="flex flex-wrap gap-1.5 sm:gap-2">
      {times.map((t) => (
        <SlotChip
          key={t}
          time={t}
          endTime={endTimeFor(t)}
          mark={slots?.[t]}
          onChange={(m) => onChange(t, m)}
          beginDrag={beginDrag}
          paintChip={paintChip}
          wasDraggedRef={wasDraggedRef}
          testId={`${testIdPrefix}-${t.replace(":", "")}`}
        />
      ))}
    </div>
  );
}

function SlotChip({ time, endTime, mark, onChange, beginDrag, paintChip, wasDraggedRef, testId }) {
  const [open, setOpen] = useState(false);
  const base = "w-12 h-12 sm:w-14 sm:h-14 rounded-2xl border flex items-center justify-center font-heading text-sm transition-all touch-none";
  const styles =
    mark === "present"
      ? "bg-[#E8F5E9] border-[#2E7D32] text-[#2E7D32]"
      : mark === "late"
        ? "bg-[#FFF3E0] border-[#E65100] text-[#E65100] font-bold"
        : "bg-white border-brand-purple/15 text-brand-inkMuted hover:border-brand-purple/40 hover:bg-brand-purpleLight";

  const choose = (m) => { onChange(m); setOpen(false); };

  const handlePointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return; // primary button only
    beginDrag && beginDrag(time, mark);
  };
  const handlePointerEnter = (e) => {
    // Only paint when the primary button is being held.
    if (!e.buttons) return;
    paintChip && paintChip(time, mark);
  };
  const handleClick = (e) => {
    if (wasDraggedRef && wasDraggedRef()) {
      // A drag just finished — suppress popover and click behaviour.
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={(v) => {
      if (v && wasDraggedRef && wasDraggedRef()) return; // don't open after drag
      setOpen(v);
    }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${base} ${styles}`}
          aria-label={`Attendance slot ${time}`}
          data-testid={testId}
          onPointerDown={handlePointerDown}
          onPointerEnter={handlePointerEnter}
          onClick={handleClick}
        >
          {mark === "present" ? (
            <Check className="w-5 h-5" strokeWidth={3} />
          ) : mark === "late" ? (
            "L"
          ) : (
            <span className="text-[11px] sm:text-xs font-body">{time}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 rounded-2xl p-2" align="center">
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-brand-inkMuted">
          <Clock className="w-4 h-4" />
          {endTime ? <span>{time} – {endTime}</span> : <span>{time}</span>}
        </div>
        <SlotOption
          icon={<Check className="w-4 h-4 text-[#2E7D32]" strokeWidth={3} />}
          label="Present"
          active={mark === "present"}
          onClick={() => choose("present")}
          testId={`${testId}-present`}
        />
        <SlotOption
          icon={<span className="text-[#E65100] font-bold w-4 text-center">L</span>}
          label="Late"
          active={mark === "late"}
          onClick={() => choose("late")}
          testId={`${testId}-late`}
        />
        <SlotOption
          icon={<span className="w-4" />}
          label="Clear"
          onClick={() => choose(null)}
          testId={`${testId}-clear`}
          muted
        />
      </PopoverContent>
    </Popover>
  );
}

function SlotOption({ icon, label, active, onClick, testId, muted }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-body hover:bg-brand-purpleLight transition-colors ${
        active ? "bg-brand-purpleLight/60" : ""
      } ${muted ? "text-brand-inkMuted" : "text-foreground"}`}
      data-testid={testId}
    >
      {icon} {label}
    </button>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1">Attendance is scoped per branch. Use the dropdown in the header.</p>
    </div>
  );
}
